import React, { useState } from 'react';
import axios from './services/axiosInstance';
import { FaTimes, FaCheck, FaClock, FaCheckCircle } from 'react-icons/fa';
import './PaymentDetailsModal.css';

const PaymentDetailsModal = ({ payment, onClose, onUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [installments, setInstallments] = useState(payment.installments || []);

    const handleMarkPaid = async (installmentId) => {
        if (!window.confirm('هل أنت متأكد من تأكيد هذا الدفع؟')) {
            return;
        }

        setLoading(true);
        try {
            await axios.put(`/payments/installment/${installmentId}/paid`);

            // Update local state
            setInstallments(prev => prev.map(inst =>
                inst._id === installmentId
                    ? { ...inst, isPaid: true, paidDate: new Date() }
                    : inst
            ));

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Error marking installment as paid:', error);
            alert('حدث خطأ أثناء تأكيد الدفع');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('ar-TN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return `${amount.toLocaleString('ar-TN')} TND`;
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { class: 'badge-warning', text: 'نشط', icon: FaClock },
            completed: { class: 'badge-success', text: 'مكتمل', icon: FaCheckCircle },
            overdue: { class: 'badge-danger', text: 'متأخر', icon: FaClock }
        };
        const badge = badges[status] || badges.active;
        const Icon = badge.icon;
        return (
            <span className={`badge ${badge.class}`}>
                <Icon /> {badge.text}
            </span>
        );
    };

    const isOverdue = (dueDate, isPaid) => {
        return !isPaid && new Date(dueDate) < new Date();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-container payment-details-modal">
                <div className="modal-header">
                    <h2>تفاصيل الدفع</h2>
                    <button className="btn-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Student Info */}
                    <div className="info-section">
                        <h3>معلومات الطالب</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>الاسم:</label>
                                <span>{payment.studentId?.username}</span>
                            </div>
                            <div className="info-item">
                                <label>البريد الإلكتروني:</label>
                                <span>{payment.studentId?.email || 'غير متوفر'}</span>
                            </div>
                            <div className="info-item">
                                <label>هاتف الطالب:</label>
                                <span>{payment.studentId?.phoneNumber || 'غير متوفر'}</span>
                            </div>
                            <div className="info-item">
                                <label>هاتف ولي الأمر:</label>
                                <span>{payment.studentId?.parentPhoneNumber || 'غير متوفر'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Overview */}
                    <div className="info-section">
                        <h3>ملخص الدفع</h3>
                        <div className="payment-summary">
                            <div className="summary-card">
                                <div className="summary-label">المبلغ الإجمالي</div>
                                <div className="summary-value">{formatCurrency(payment.totalAmount)}</div>
                            </div>
                            <div className="summary-card success">
                                <div className="summary-label">المبلغ المدفوع</div>
                                <div className="summary-value">{formatCurrency(payment.paidAmount)}</div>
                            </div>
                            <div className="summary-card warning">
                                <div className="summary-label">المبلغ المتبقي</div>
                                <div className="summary-value">{formatCurrency(payment.remainingAmount)}</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-label">الحالة</div>
                                <div className="summary-value">{getStatusBadge(payment.status)}</div>
                            </div>
                        </div>

                        <div className="info-grid mt-3">
                            <div className="info-item">
                                <label>تاريخ الاستحقاق:</label>
                                <span>{formatDate(payment.deadlineDate)}</span>
                            </div>
                            <div className="info-item">
                                <label>عدد الأقساط:</label>
                                <span>{payment.numberOfInstallments} قسط</span>
                            </div>
                        </div>
                    </div>

                    {/* Installments List */}
                    <div className="info-section">
                        <h3>قائمة الأقساط</h3>
                        <div className="installments-list">
                            {installments.map((inst) => (
                                <div
                                    key={inst._id}
                                    className={`installment-item ${inst.isPaid ? 'paid' : ''} ${isOverdue(inst.dueDate, inst.isPaid) ? 'overdue' : ''}`}
                                >
                                    <div className="installment-header">
                                        <div className="installment-number">
                                            القسط {inst.installmentNumber}
                                        </div>
                                        <div className="installment-status">
                                            {inst.isPaid ? (
                                                <span className="paid-badge">
                                                    <FaCheckCircle /> مدفوع
                                                </span>
                                            ) : isOverdue(inst.dueDate, inst.isPaid) ? (
                                                <span className="overdue-badge">
                                                    متأخر
                                                </span>
                                            ) : (
                                                <span className="pending-badge">
                                                    قيد الانتظار
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="installment-details">
                                        <div className="detail-row">
                                            <span className="detail-label">المبلغ:</span>
                                            <span className="detail-value">{formatCurrency(inst.amount)}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">تاريخ الاستحقاق:</span>
                                            <span className="detail-value">{formatDate(inst.dueDate)}</span>
                                        </div>
                                        {inst.isPaid && (
                                            <div className="detail-row">
                                                <span className="detail-label">تاريخ الدفع:</span>
                                                <span className="detail-value success">
                                                    {formatDate(inst.paidDate)}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {!inst.isPaid && (
                                        <div className="installment-actions">
                                            <button
                                                className="btn-mark-paid"
                                                onClick={() => handleMarkPaid(inst._id)}
                                                disabled={loading}
                                            >
                                                <FaCheck /> تأكيد الدفع
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>
                        إغلاق
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentDetailsModal;
